import { OfferingAllowance, OfferingAllowances, Sku } from "../model/data";
import type {
    CloudOfferingKey,
    OfferingSKUMap
} from "@/components/wpl/pricing/src/price-finder/constants.tsx";

import type { CCPOfferingContent } from "@/contentful/product-catalog.tsx";

const USAGE_KEY_MAP: Record<string, keyof OfferingAllowance> = {
    "ai-credit": "aiCredit",
    "indexed-document": "indexedDocument"
};

const CYCLE_MAP: Record<string, keyof OfferingAllowance["aiCredit"]> = {
    MONTHLY: "monthly",
    ANNUAL: "annual"
};

export const transform = (
    offerings: OfferingSKUMap<CloudOfferingKey, CCPOfferingContent>
): OfferingAllowances => {
    const result = {} as Record<string, OfferingAllowance>;

    for (const [sku, offering] of Object.entries(offerings)) {
        const allowance: OfferingAllowance = {
            aiCredit: { monthly: 0, annual: 0 },
            indexedDocument: { monthly: 0, annual: 0 }
        };

        const features = offering?.entitlementTemplate_data?.features ?? [];
        for (const feature of features) {
            applyFeatureAllowances(allowance, feature.value?.allowances ?? []);
        }

        result[sku as Sku] = allowance;
    }

    return result as OfferingAllowances;
};

const applyCycles = (
    target: OfferingAllowance[keyof OfferingAllowance],
    cycles: NonNullable<
        CCPOfferingContent["entitlementTemplate_data"]
    >["features"][number]["value"]["allowances"][number]["config"]["cycles"]
): void => {
    for (const cycle of cycles) {
        const period = CYCLE_MAP[cycle.cycle];
        if (period) {
            target[period] += cycle.amount;
        }
    }
};

const applyFeatureAllowances = (
    allowance: OfferingAllowance,
    featureAllowances: NonNullable<
        CCPOfferingContent["entitlementTemplate_data"]
    >["features"][number]["value"]["allowances"]
): void => {
    for (const featureAllowance of featureAllowances) {
        const usageKey = featureAllowance.config?.usageKey;
        const mappedKey = usageKey ? USAGE_KEY_MAP[usageKey] : undefined;
        if (!mappedKey) {
            continue;
        }
        applyCycles(
            allowance[mappedKey],
            featureAllowance.config?.cycles ?? []
        );
    }
};
